import { baseApi } from "./baseApi";

// ─── MCP tools ──────────────────────────────────────────────
// shapes mirror the backend: platform tools come from mcp/server.ts `listMcpTools`,
// `live` is the agent runtime's own `/api/mcp` inventory (or { error } when offline).

export interface McpToolInfo {
  name: string;
  description: string;
  inputSchema: Record<string, unknown>;
}

export interface McpServerInfo {
  name: string;
  url: string | null;
  connected: boolean;
  tools: McpToolInfo[];
}

export interface McpLiveInfo {
  servers?: McpServerInfo[];
  builtinToolCount?: number;
  error?: string;
}

export interface AgentToolsData {
  platformTools: McpToolInfo[];
  live: McpLiveInfo | null;
}

export interface AgentToolsResponse {
  success: boolean;
  data: AgentToolsData;
}

// ─── event preview / fire ───────────────────────────────────

// the exact payload the backend constructs and sends to the agent
export interface IngestExternalPayloadPreview {
  content: string;
  source: string;
  urgency?: "low" | "normal" | "high" | "critical";
  attachments?: Array<{ type: string; data: string; name?: string }>;
  metadata?: Record<string, unknown>;
  event_type?: string;
  deadline?: string;
  pace?: string;
  custom_instructions?: string;
  extra_tools?: string[];
  remove_tools?: string[];
}

export interface AgentEventInput {
  type: string;
  actorUsername?: string;
  actorId?: string;
  entityId?: string;
  metadata?: Record<string, unknown>;
}

export interface AgentEventPreviewResponse {
  success: boolean;
  data: {
    // false when no agent builder exists for this event type (payload === null)
    handled: boolean;
    payload: IngestExternalPayloadPreview | null;
  };
}

export interface AgentEventFireResponse {
  success: boolean;
  data?: { sent: boolean; payload: IngestExternalPayloadPreview };
  sent?: boolean;
  message?: string;
}

export const agentInspectorAdminApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAgentTools: builder.query<AgentToolsResponse, void>({
      query: () => "/v2/admin/agent-inspector/tools",
      providesTags: ["AgentInspector"],
    }),
    previewAgentEvent: builder.mutation<AgentEventPreviewResponse, AgentEventInput>({
      query: (body) => ({
        url: "/v2/admin/agent-inspector/preview",
        method: "POST",
        body,
      }),
    }),
    fireAgentEvent: builder.mutation<AgentEventFireResponse, AgentEventInput>({
      query: (body) => ({
        url: "/v2/admin/agent-inspector/fire",
        method: "POST",
        body,
      }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetAgentToolsQuery,
  usePreviewAgentEventMutation,
  useFireAgentEventMutation,
} = agentInspectorAdminApi;
